import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Loader from 'react-loader-spinner'
import { GlgTooltipIcon } from '../../shared/components'
import EventHeader from './event_header'
import ChevronIcon from './chevron_icon'
import EventSummaryTable from './event_summary_table'
import LeagueSummaryTable from './league_summary_table'
import { summaryTitle, summaryMetric, isTeamSummary } from '../actions/summaries_index'
import { themedTextClass } from '../utils/css_utils'
import HeaderActionsContainer from '../containers/header_actions_container'
import { VIEW_MODES } from '../constants'
import { componentText } from '../utils/i18n_utils'

// One tile for all four summaries and all view modes. Web: collapsed header,
// click fetches and expands inline. Mobile/landscape: rendered as the selected
// view — header opens the rounds/leaderboards modal. Portrait only: Expand/
// Collapse View toggles the per-tournament columns (landscape and web always
// show them). Events and trips render EventSummaryTable; a League's
// round-scoped payload renders LeagueSummaryTable instead - totals only,
// tournaments behind the name (RTM-10251) - so the toggle has nothing to
// expand there.
const SummaryTile = ({
  summaryKey,
  viewMode,
  loading,
  error,
  data,
  isOpen = false,
  fetchSummary,
  closeSummary,
  handleMobileClick,
  isManagerSite,
  currencySymbol,
  purseDecimalPlaces,
}) => {
  const isMobileView = [ VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE ].includes(viewMode)
  const [ expandedOverride, setExpandedOverride ] = useState(null)

  const isOpenEffective = isMobileView || isOpen
  const isExpanded = viewMode === VIEW_MODES.MOBILE ? (expandedOverride ?? false) : true

  useEffect(() => { setExpandedOverride(null) }, [ viewMode ])

  const title = summaryTitle(summaryKey)
  const tournaments = data?.tournaments ?? []
  const isLeagueSummary = data?.roundScoped === true

  return (
    <li id={`summary-${summaryKey}`} className="lbv2__event lbv2__summary">
      <EventHeader isOpen={isOpenEffective}>
        <button
          className="lbv2__event-btn"
          aria-expanded={isOpenEffective}
          onClick={isMobileView ? handleMobileClick : () => (isOpen ? closeSummary() : fetchSummary())}
        >
          {viewMode === VIEW_MODES.WEB && <ChevronIcon isOpen={isOpen} />}
          {title}
          {isMobileView && <ChevronIcon isOpen={false} />}
        </button>
        {(loading || error) && (
          <div className="lbv2__state-info">
            {loading && (
              <div className="lbv2__loader">
                <Loader type="Oval" color={'#333F48'} height={20} width={12} />
              </div>
            )}
            {error && (
              <div className="lbv2__error">
                <GlgTooltipIcon tooltip={error} />
                <div>{componentText('shared.error', 'error')}</div>
              </div>
            )}
          </div>
        )}
        {isMobileView && !isManagerSite && <HeaderActionsContainer viewMode={viewMode} />}
      </EventHeader>
      {viewMode === VIEW_MODES.MOBILE && !isLeagueSummary && tournaments.length > 0 && (
        <div className="lbv2__expand">
          <button
            className={`lbv2__expand-btn ${themedTextClass(isManagerSite)}`}
            onClick={() => setExpandedOverride(!isExpanded)}
          >
            {isExpanded
              ? componentText('event.collapse_view', 'Collapse View')
              : componentText('event.expand_view', 'Expand View')}
          </button>
        </div>
      )}
      {isOpenEffective && data && isLeagueSummary && (
        <LeagueSummaryTable
          metric={summaryMetric(summaryKey)}
          isTeam={isTeamSummary(summaryKey)}
          tournaments={tournaments}
          rows={data.rows}
          currencySymbol={currencySymbol}
          purseDecimalPlaces={purseDecimalPlaces}
          isManagerSite={isManagerSite}
        />
      )}
      {isOpenEffective && data && !isLeagueSummary && (
        <EventSummaryTable
          metric={summaryMetric(summaryKey)}
          isTeam={isTeamSummary(summaryKey)}
          expanded={isExpanded}
          tournaments={tournaments}
          rows={data.rows}
          currencySymbol={currencySymbol}
          purseDecimalPlaces={purseDecimalPlaces}
          viewMode={viewMode}
        />
      )}
    </li>
  )
}

SummaryTile.propTypes = {
  summaryKey: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  loading: PropTypes.bool,
  error: PropTypes.string,
  data: PropTypes.object,
  isOpen: PropTypes.bool,
  fetchSummary: PropTypes.func.isRequired,
  closeSummary: PropTypes.func,
  handleMobileClick: PropTypes.func,
  isManagerSite: PropTypes.bool,
  currencySymbol: PropTypes.string,
  purseDecimalPlaces: PropTypes.number,
}

export default SummaryTile
